import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message as antMessage,
} from "antd";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuth } from "shared/contexts/AuthProvider";
import { replicationAgentsService } from "../services/orgStorageService";
import { getErrorDetail } from "./OrgStorageSection";
import { BUTTON_STYLES } from "../constants";
import { formatDateTime, parseApiInstant } from "shared/utils/datetime";

const { Text, Paragraph } = Typography;

const AGENT_ONLINE_WINDOW_MS = 5 * 60 * 1000;

/**
 * Replication-agent keys of the organization: credentials for org-side
 * workers that copy/delete MCAP replicas on storages the platform cannot
 * reach (unmanaged S3, filesystem). Tokens are shown exactly once.
 *
 * @param {string} orgId - Organization ID
 * @param {Array} storages - Storage rows (for the optional scope selector)
 * @param {Function} translate - Translation function
 */
export const ReplicationAgentsCard = ({
  orgId,
  storages = [],
  translate = (key) => key,
}) => {
  const { authFetch } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState([]);
  const [issuedToken, setIssuedToken] = useState(null);

  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await replicationAgentsService.list(orgId, authFetch);
      setAgents(response.data || []);
    } catch (error) {
      antMessage.error(
        translate("organizations.agents.loadFailed") + getErrorDetail(error),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, authFetch]);

  useEffect(() => {
    if (!orgId) return;
    loadAgents();
  }, [orgId, loadAgents]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      antMessage.warning(translate("organizations.agents.nameRequired"));
      return;
    }
    try {
      setCreating(true);
      const response = await replicationAgentsService.create(
        orgId,
        { name: newName.trim(), storage_ids: newScope },
        authFetch,
      );
      setIssuedToken(response.data);
      setNewName("");
      setNewScope([]);
      loadAgents();
    } catch (error) {
      antMessage.error(
        translate("organizations.agents.createFailed") + getErrorDetail(error),
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (agent) => {
    try {
      await replicationAgentsService.remove(orgId, agent.agent_id, authFetch);
      antMessage.success(translate("organizations.agents.revokeSuccess"));
      loadAgents();
    } catch (error) {
      antMessage.error(
        translate("organizations.agents.revokeFailed") + getErrorDetail(error),
      );
    }
  };

  const storageName = (id) =>
    storages.find((s) => s.storage_id === id)?.name || id;

  const columns = [
    {
      title: translate("organizations.agents.name"),
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        const lastSeen = parseApiInstant(record.last_seen_at);
        const online =
          lastSeen && Date.now() - lastSeen.getTime() < AGENT_ONLINE_WINDOW_MS;
        return (
          <Space>
            <Badge status={online ? "success" : "default"} />
            <Text strong>{name}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.token_prefix}…
            </Text>
          </Space>
        );
      },
    },
    {
      title: translate("organizations.agents.scope"),
      dataIndex: "storage_ids",
      key: "scope",
      render: (ids) =>
        ids && ids.length
          ? ids.map(storageName).join(", ")
          : translate("organizations.agents.scopeAll"),
    },
    {
      title: translate("organizations.agents.lastSeen"),
      dataIndex: "last_seen_at",
      key: "last_seen",
      render: (value) =>
        value
          ? formatDateTime(value)
          : translate("organizations.agents.never"),
    },
    {
      title: translate("organizations.storages.actions"),
      key: "actions",
      render: (_, agent) => (
        <Popconfirm
          title={translate("organizations.agents.revokeConfirm")}
          onConfirm={() => handleRevoke(agent)}
        >
          <Button size="small" danger>
            {translate("organizations.agents.revoke")}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      size="small"
      title={translate("organizations.agents.title")}
      style={{ marginBottom: 16 }}
    >
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 8, fontSize: 12 }}
      >
        {translate("organizations.agents.subtitle")}
      </Text>
      <Table
        size="small"
        rowKey="agent_id"
        loading={loading}
        columns={columns}
        dataSource={agents}
        pagination={false}
        locale={{ emptyText: translate("organizations.agents.empty") }}
        style={{ marginBottom: 12 }}
      />
      <Space wrap>
        <Input
          style={{ minWidth: 160 }}
          placeholder={translate("organizations.agents.namePlaceholder")}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 200 }}
          placeholder={translate("organizations.agents.scopeAll")}
          value={newScope}
          onChange={setNewScope}
          options={storages
            .filter((s) => s.storage_class !== "system")
            .map((s) => ({ value: s.storage_id, label: s.name }))}
        />
        <Button icon={<PlusOutlined />} loading={creating} onClick={handleCreate} style={BUTTON_STYLES.add}>
          {translate("organizations.agents.create")}
        </Button>
      </Space>

      <Modal
        open={!!issuedToken}
        title={translate("organizations.agents.tokenTitle")}
        onCancel={() => setIssuedToken(null)}
        footer={[
          <Button key="close" onClick={() => setIssuedToken(null)}>
            {translate("organizations.agents.tokenClose")}
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={translate("organizations.agents.tokenWarning")}
        />
        <Paragraph
          copyable={{
            text: issuedToken?.token,
            icon: <CopyOutlined />,
          }}
          code
          style={{ wordBreak: "break-all" }}
        >
          {issuedToken?.token}
        </Paragraph>
      </Modal>
    </Card>
  );
};
